import { Stack } from '@mui/material';
import { useCallback, useMemo, useRef } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { getIntegrationEntitiesFromEntity } from '@/modules/assetProviderIntegrations/shared/utils';
import { FullScreenStructuredAssetsModalForm } from '@/modules/assets/FullScreenStructuredAssetsModal/FullScreenStructuredAssetsModal.types';
import { createUpdateEntityAccountInput } from '@/modules/assets/FullScreenStructuredAssetsModal/FullScreenStructuredAssetsModal.utils';
import { SideDrawer } from '@/modules/common/SideDrawer/SideDrawer';
import { useUpdateEntityMutation } from '@/modules/entities/graphql/UpdateEntity.generated';
import {
  StructuredAssetsSubform_AssetFragment,
  StructuredAssetsSubform_ValuationFragment,
} from '@/modules/entities/StructuredAssetsSubform/graphql/StructuredAssetsSubform.generated';
import { StructuredAsset } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import {
  decodeSitusKind,
  filterEmptyStructuredAssetRows,
  getFormAssetsFromAssets,
} from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.utils';
import {
  getAssetTaxSitusEventAction,
  useTrackAssetTaxSitusEvent,
} from '@/modules/entities/StructuredAssetsSubform/useTrackAssetTaxSitusEvent';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { AssetHoldingsFormShape } from '@/modules/estate/components/AddHoldingsFormModal/AddHoldingsFormModal.types';
import { Operations as EstatePageOperations } from '@/modules/estate/graphql/EstatePageData.generated';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';

import { EditHoldingsForm, EditHoldingsFormRef } from './EditHoldingsForm';

export interface EditAssetSideDrawerProps {
  /**
   * Whether the drawer is open
   */
  isOpen: boolean;
  /**
   * Function to call when closing the drawer
   */
  onClose: () => void;
  /**
   * The entity being edited
   */
  entity: EstatePage_EntityFragment;
  /**
   * Existing assets to pre-populate the form
   */
  existingAssets: StructuredAssetsSubform_AssetFragment[];
  /**
   * The valuation containing the assets
   */
  valuation: StructuredAssetsSubform_ValuationFragment | null;
}

/**
 * Side drawer for editing assets
 */
export function EditAssetSideDrawer({
  isOpen,
  onClose,
  entity,
  existingAssets,
  valuation,
}: EditAssetSideDrawerProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const trackAssetTaxSitusEvent = useTrackAssetTaxSitusEvent();
  const { assetClassesById } = useTenantDetailsContext();
  const formRef = useRef<EditHoldingsFormRef>(null);

  const initialAssets = useMemo((): StructuredAsset[] => {
    return getFormAssetsFromAssets(existingAssets, {
      activeClientIntegrationType: null,
    });
  }, [existingAssets]);

  const { householdId, householdIntegrationLink } =
    useHouseholdDetailsContext();
  const [updateEntityMutation, { loading }] = useUpdateEntityMutation();

  const handleSubmit = useCallback(
    async (data: AssetHoldingsFormShape) => {
      try {
        // Get entity parameters
        const entityId = entity.id;
        const subtypeId = entity.subtype.id;
        const entityType = getEntityTypeFromEntityKind(entity.kind);

        if (!entityType) {
          throw new Error('Unable to determine entity type');
        }

        if (!householdId) {
          throw new Error('Missing required householdId');
        }

        // Preserve any integration links on the entity so that saving manual
        // assets keeps (rather than drops) the integration assets on the new
        // valuation. Passing the same ids as previous means nothing is
        // added/removed — we're only re-linking the existing integration.
        const integrationEntityIds = getIntegrationEntitiesFromEntity(
          entity
        ).map((ie) => ie.id);

        // Transform the form data to the expected format
        const transformedFormValues: FullScreenStructuredAssetsModalForm = {
          structuredAssetsSubform: data.structuredAssetsSubform,
          valuationAsOfDate: data.valuationAsOfDate,
          // Add required fields for the form type
          integrationEntityIds,
          _previousIntegrationEntityIds: integrationEntityIds,
          // When integration-linked, this ensures the edited manual assets are
          // kept alongside the integration assets on the new valuation.
          allowAdditionalManualAssets: true,
          showAllIntegratedHoldings: false,
          linkToAllAddeparEntities: false,
          showBalanceSheetCategoryOverrideChooser: false,
          balanceSheetCategoryOverrideId: '', // Default empty string since it's required as string
          showEntityNameOnBalanceSheet: false,
          showCurrentDiscountedValueOnBalanceSheet: false,
          currentDiscountedValuePercentage: null,
          liquidityStatus: entity.liquidityStatus,
        };

        // Create the update input
        const updateEntityAccountInput = createUpdateEntityAccountInput(
          transformedFormValues,
          {
            activeClientIntegrationKind: householdIntegrationLink,
            previousValuation: valuation,
            entityId,
            entityType,
            subtypeId,
            householdId,
          }
        );

        // Submit the mutation
        await updateEntityMutation({
          variables: {
            input: updateEntityAccountInput,
          },
          refetchQueries: [EstatePageOperations.Query.EstatePageData],
          onCompleted: () => {
            for (const asset of filterEmptyStructuredAssetRows(
              data.structuredAssetsSubform.assets
            )) {
              const action = getAssetTaxSitusEventAction(
                asset._originalTaxSitus,
                asset.taxSitus
              );
              if (action) {
                trackAssetTaxSitusEvent(action, {
                  source: 'edit_asset_side_drawer',
                  householdId,
                  entityId,
                  assetClass: assetClassesById[asset.categoryId]?.displayName,
                  situsKind: decodeSitusKind(asset.taxSitus),
                  entityType,
                });
              }
            }
            showFeedback('Assets updated successfully', {
              variant: 'success',
            });
            onClose();
          },
          onError: (error) => {
            reportError('Failed to update assets', error);
          },
        });
      } catch (error) {
        reportError('Failed to update assets', error as Error);
      }
    },
    [
      onClose,
      reportError,
      showFeedback,
      entity,
      householdId,
      householdIntegrationLink,
      valuation,
      updateEntityMutation,
      trackAssetTaxSitusEvent,
      assetClassesById,
    ]
  );

  const handleSave = useCallback(() => {
    formRef.current?.submit();
  }, []);

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      heading="Manage manually tracked assets"
      data-testid="edit-asset-side-drawer"
      body={
        <Stack gap={2} mt={2} width="100%" flex={1}>
          <EditHoldingsForm
            ref={formRef}
            holdingsGroup={entity}
            onSubmit={handleSubmit}
            disabled={loading}
            initialAssets={initialAssets}
            valuation={valuation}
          />
        </Stack>
      }
      footer={
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            loading={loading}
            disabled={loading}
          >
            Save changes
          </Button>
        </Stack>
      }
    />
  );
}
